import type { AnalyticsResponse } from '../types';
import { formatCurrency } from '../utils/format';

interface Props {
  data: AnalyticsResponse;
}

export function SummaryCards({ data }: Props) {
  const { total_spent, total_income, total_invested, potential_savings } = data.summary;

  const cards = [
    {
      label: 'Total Spent',
      value: formatCurrency(total_spent),
      sub: 'Expenditure this period',
      accent: 'text-rose-400',
      border: 'border-rose-500/20',
      bg: 'bg-rose-500/5',
    },
    {
      label: 'Total Income',
      value: formatCurrency(total_income),
      sub: 'Credits this period',
      accent: 'text-emerald-400',
      border: 'border-emerald-500/20',
      bg: 'bg-emerald-500/5',
    },
    {
      label: 'Total Invested',
      value: formatCurrency(total_invested ?? 0),
      sub: 'FD / SIP / Mutual funds',
      accent: 'text-amber-400',
      border: 'border-amber-500/20',
      bg: 'bg-amber-500/5',
    },
    {
      label: 'Potential Savings',
      value: formatCurrency(potential_savings),
      sub: potential_savings >= 0 
        ? 'Unallocated income after spending' 
        : 'Overspent by ' + formatCurrency(Math.abs(potential_savings)),
      accent: potential_savings >= 0 ? 'text-cyan-400' : 'text-rose-400',
      border: potential_savings >= 0 ? 'border-cyan-500/20' : 'border-rose-500/20',
      bg: potential_savings >= 0 ? 'bg-cyan-500/5' : 'bg-rose-500/5',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className={`group relative overflow-hidden rounded-xl border ${card.border} ${card.bg} p-4 transition hover:border-zinc-700`}
        >
          <div className="flex items-start justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              {card.label}
            </span>
          </div>
          <p className={`mt-2 text-2xl font-bold tracking-tight ${card.accent}`}>
            {card.value}
          </p>
          <p className="mt-1 text-[11px] text-zinc-600">
            {card.sub}
          </p>
          {card.label === 'Potential Savings' && (
            <div className="mt-2 text-[10px] text-zinc-700 leading-relaxed">
              {potential_savings >= 0
                ? 'This is the amount remaining after all recorded spending. Consider moving it to savings or investments.'
                : 'Your spending exceeded income this period. Review non-essential expenses to bring this back to positive.'}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
